import React, { useState } from 'react';
import { Web3Provider, useWeb3 } from './contexts/Web3Context';
import UserRegistration from './components/UserRegistration';
import UserStats from './components/UserStats';
import CreateSellOrder from './components/CreateSellOrder';
import BuyOrder from './components/BuyOrder';
import OrderManagement from './components/OrderManagement';
import Staking from './components/Staking';
import AdminPanel from './components/AdminPanel';
import Analytics from './components/Analytics';
import GameDeposit from './components/GameDeposit';
import './App.css';

function AppContent() {
  const { account, connectWallet, disconnectWallet, switchToBSCMainnet, loading, error, isAdmin, getBalances } = useWeb3();
  const [activeView, setActiveView] = useState('dashboard');
  const [balances, setBalances] = useState({ bnb: '0', usdt: '0', token: '0' });
  const [menuOpen, setMenuOpen] = useState(false);

  React.useEffect(() => {
    if (account) {
      loadBalances();
    }
  }, [account]);

  const loadBalances = async () => {
    const bal = await getBalances();
    setBalances(bal);
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return <Analytics />;
      case 'register':
        return <UserRegistration />;
      case 'stats':
        return <UserStats />;
      case 'sell':
        return <CreateSellOrder />;
      case 'buy':
        return <BuyOrder />;
      case 'orders':
        return <OrderManagement />;
      case 'staking':
        return <Staking />;
      case 'game':
        return <GameDeposit />;
      case 'admin':
        return <AdminPanel />;
      default:
        return <Analytics />;
    }
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <h1>🚀 P2P Trade Platform</h1>
            <button className="menu-toggle" onClick={() => setMenuOpen(!menuOpen)}>
              ☰
            </button>
          </div>

          <div className="wallet-section">
            {account ? (
              <div className="wallet-info">
                <div className="balances">
                  <span className="balance-item">BNB: {parseFloat(balances.bnb).toFixed(4)}</span>
                  <span className="balance-item">USDT: {parseFloat(balances.usdt).toFixed(2)}</span>
                  <span className="balance-item">Token: {parseFloat(balances.token).toFixed(2)}</span>
                </div>
                <div className="wallet-address">
                  <span className="address">{account.slice(0, 6)}...{account.slice(-4)}</span>
                  {isAdmin && <span className="admin-badge">Admin</span>}
                  <button className="btn-disconnect" onClick={disconnectWallet}>
                    Disconnect
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn-connect" onClick={connectWallet}>
                Connect Wallet
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="app-body">
        <nav className={`sidebar ${menuOpen ? 'open' : ''}`}>
          <div className="nav-section">
            <h3>Platform</h3>
            <button
              className={activeView === 'dashboard' ? 'active' : ''}
              onClick={() => { setActiveView('dashboard'); setMenuOpen(false); }}
            >
              📊 Dashboard
            </button>
            <button
              className={activeView === 'register' ? 'active' : ''}
              onClick={() => { setActiveView('register'); setMenuOpen(false); }}
            >
              📝 Register
            </button>
            <button
              className={activeView === 'stats' ? 'active' : ''}
              onClick={() => { setActiveView('stats'); setMenuOpen(false); }}
            >
              📈 My Stats
            </button>
          </div>

          <div className="nav-section">
            <h3>Trading</h3>
            <button
              className={activeView === 'buy' ? 'active' : ''}
              onClick={() => { setActiveView('buy'); setMenuOpen(false); }}
            >
              💰 Buy Tokens
            </button>
            <button
              className={activeView === 'sell' ? 'active' : ''}
              onClick={() => { setActiveView('sell'); setMenuOpen(false); }}
            >
              💸 Sell Tokens
            </button>
            <button
              className={activeView === 'orders' ? 'active' : ''}
              onClick={() => { setActiveView('orders'); setMenuOpen(false); }}
            >
              📋 My Orders
            </button>
          </div>

          <div className="nav-section">
            <h3>Earn</h3>
            <button
              className={activeView === 'staking' ? 'active' : ''}
              onClick={() => { setActiveView('staking'); setMenuOpen(false); }}
            >
              🔒 Staking
            </button>
            <button
              className={activeView === 'game' ? 'active' : ''}
              onClick={() => { setActiveView('game'); setMenuOpen(false); }}
            >
              🎮 Game Deposit
            </button>
          </div>

          {isAdmin && (
            <div className="nav-section">
              <h3>Admin</h3>
              <button
                className={activeView === 'admin' ? 'active' : ''}
                onClick={() => { setActiveView('admin'); setMenuOpen(false); }}
              >
                ⚙️ Admin Panel
              </button>
            </div>
          )}
        </nav>

        <main className="main-content">
          {loading ? (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Connecting to blockchain...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <h2>⚠️ Wrong Network</h2>
              <p>{error}</p>
              <button className="btn-connect-large" style={{ marginTop: '1.5rem' }} onClick={switchToBSCMainnet}>
                ⚡ Switch Wallet to BSC Mainnet (Chain ID 56)
              </button>
            </div>
          ) : !account ? (
            <div className="welcome-container">
              <h2>Welcome to P2P Trade Platform</h2>
              <p>Connect your wallet to start trading, staking, and earning rewards!</p>
              <button className="btn-connect-large" onClick={connectWallet}>
                Connect Wallet
              </button>
            </div>
          ) : (
            renderContent()
          )}
        </main>
      </div>

      <footer className="app-footer">
        <p>© 2024 P2P Trade Platform. All rights reserved.</p>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Web3Provider>
      <AppContent />
    </Web3Provider>
  );
}

export default App;